import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Check } from 'lucide-react';
import * as React from 'react';

import { Spinner } from '@apps/web/components/ui/spinner';
import { cn } from '@apps/web/lib/utils';

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all cursor-pointer disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive pointer-coarse:min-h-11 pointer-coarse:min-w-11",
    {
        variants: {
            variant: {
                default:
                    'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
                destructive:
                    'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
                outline:
                    'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
                secondary:
                    'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
                ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
                link: 'text-foreground underline-offset-4 hover:underline',
            },
            size: {
                default: 'h-9 px-4 py-2 has-[>svg]:px-3',
                sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
                lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
                icon: 'size-9',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
);

// Mirrors a mutation's lifecycle (react-query's `mutation.status` can be
// passed directly). 'pending' shows a spinner and disables the button;
// completing as 'success' pops a transient checkmark where the spinner was.
export type ButtonStatus = 'idle' | 'pending' | 'success' | 'error';

// How long the success checkmark stays visible. Exported so wrappers that
// unmount their button after a save (e.g. settings action bars) can hold it
// on screen for the full beat.
export const BUTTON_SUCCESS_DISPLAY_MS = 1500;

function Button({
    className,
    variant,
    size,
    asChild = false,
    status,
    disabled,
    children,
    ...props
}: React.ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
        asChild?: boolean;
        status?: ButtonStatus;
    }) {
    const [showCheck, setShowCheck] = React.useState(false);
    const previousStatus = React.useRef(status);

    React.useEffect(() => {
        const wasPending = previousStatus.current === 'pending';
        previousStatus.current = status;
        // Only celebrate a pending → success transition, so a button mounted
        // with a stale 'success' status (e.g. a reopened dialog) stays quiet.
        if (status !== 'success' || !wasPending) {
            setShowCheck(false);
            return;
        }
        setShowCheck(true);
        const timer = setTimeout(
            () => setShowCheck(false),
            BUTTON_SUCCESS_DISPLAY_MS,
        );
        return () => clearTimeout(timer);
    }, [status]);

    // Slot requires exactly one element child, so status icons only render
    // on the plain-button path. Slot's prop type also has no `disabled`, so
    // it rides along in the spread like any other pass-through prop.
    if (asChild) {
        const slotProps = { disabled, children, ...props };
        return (
            <Slot
                data-slot="button"
                className={cn(buttonVariants({ variant, size, className }))}
                {...slotProps}
            />
        );
    }

    return (
        <button
            data-slot="button"
            className={cn(buttonVariants({ variant, size, className }))}
            disabled={disabled || status === 'pending'}
            {...props}
        >
            {status === 'pending' && (
                <Spinner size="sm" data-testid="button-pending-spinner" />
            )}
            {showCheck && (
                <Check
                    aria-hidden="true"
                    data-testid="button-success-check"
                    className="button-status-check-in size-4"
                />
            )}
            {children}
        </button>
    );
}

export { Button, buttonVariants };
